import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SimCardIcon from '@mui/icons-material/SimCard';
import { AcademySubscriptionPlan, BillingCycle } from '../types/academy';
import { academyApi } from '../services/academyApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

interface SatimPaymentModalProps {
  open: boolean;
  onClose: () => void;
  plan: AcademySubscriptionPlan | null;
  billingCycle: BillingCycle;
  onSuccess: (approvalCode: string) => void;
}

export const SatimPaymentModal: React.FC<SatimPaymentModalProps> = ({
  open,
  onClose,
  plan,
  billingCycle,
  onSuccess,
}) => {
  const { t, dir } = useLanguage();

  // Steps: 'details' | 'otp' | 'success'
  const [step, setStep] = useState<'details' | 'otp' | 'success'>('details');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Card Form State
  const [cardHolder, setCardHolder] = useState('ACADEMY ADMIN');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [orderId, setOrderId] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [approvalCode, setApprovalCode] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [simulatedOtp, setSimulatedOtp] = useState('789012');

  const amount = plan
    ? billingCycle === 'yearly'
      ? plan.PLN_PRICE_YEARLY
      : plan.PLN_PRICE_MONTHLY
    : 0;

  // Detect card type
  const isEdahabia = cardNumber.replace(/\s+/g, '').startsWith('603578');
  const cardBrand = isEdahabia ? 'EDAHABIA' : 'CIB';

  useEffect(() => {
    if (open) {
      setStep('details');
      setError(null);
      setCardNumber('');
      setCardExpiry('');
      setCardCvv('');
      setOtpCode('');
      setApprovalCode('');
    }
  }, [open]);

  // Format Card Number (adds space every 4 digits)
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 16);
    const formatted = raw.replace(/(\d{4})(?=\d)/g, '$1 ');
    setCardNumber(formatted);
  };

  // Format Expiry (MM/YY)
  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 4);
    if (raw.length >= 3) {
      setCardExpiry(`${raw.slice(0, 2)}/${raw.slice(2, 4)}`);
    } else {
      setCardExpiry(raw);
    }
  };

  // Sandbox quick-fills
  const fillTestEdahabia = () => {
    setCardNumber('6035 7812 3456 7890');
    setCardExpiry('12/28');
    setCardCvv('987');
    setCardHolder('AMINE BENALIA');
  };

  const fillTestCib = () => {
    setCardNumber('4000 1234 5678 9010');
    setCardExpiry('09/27');
    setCardCvv('123');
    setCardHolder('KARIM ZIDANE');
  };

  // Step 1: Initiate SATIM Order
  const handleProceedToOtp = async () => {
    if (!plan) return;
    const cleanNum = cardNumber.replace(/\s+/g, '');
    if (cleanNum.length !== 16) {
      setError(t('subscription.satim.invalidCardNumber', 'Numéro de carte invalide (16 chiffres requis).'));
      return;
    }
    if (!cardExpiry || cardExpiry.length < 5) {
      setError(t('subscription.satim.invalidExpiry', 'Date d’expiration invalide (MM/AA).'));
      return;
    }
    if (!cardCvv || cardCvv.length < 3) {
      setError(t('subscription.satim.invalidCvv', 'Cryptogramme visuel (CVV2) invalide.'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const initRes = await academyApi.initiateSatimPayment({
        planId: plan.PLN_IDF,
        billingCycle,
      });

      setOrderId(initRes.orderId);
      setOrderNumber(initRes.orderNumber);

      // Random OTP for 3D Secure simulation
      const generatedOtp = String(Math.floor(100000 + Math.random() * 900000));
      setSimulatedOtp(generatedOtp);
      setOtpCode(generatedOtp); // pre-filled for smooth testing
      setStep('otp');
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de l’initialisation de la session SATIM.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Confirm SATIM Order with OTP
  const handleConfirmPayment = async () => {
    if (!plan || !orderId) return;
    if (!otpCode || otpCode.length < 4) {
      setError(t('subscription.satim.invalidOtp', 'Veuillez saisir le code SMS reçu.'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cleanNum = cardNumber.replace(/\s+/g, '');
      const last4 = cleanNum.slice(-4);

      const confirmRes = await academyApi.confirmSatimPayment({
        orderId,
        cardBrand,
        last4,
        planId: plan.PLN_IDF,
        billingCycle,
      });

      if (confirmRes.success) {
        setApprovalCode(confirmRes.approvalCode);
        setStep('success');
        onSuccess(confirmRes.approvalCode);
      } else {
        setError('Le paiement a été refusé par le serveur SATIM.');
      }
    } catch (err: any) {
      setError(err?.message || 'Échec de la validation de paiement SATIM.');
    } finally {
      setLoading(false);
    }
  };

  if (!plan) return null;

  return (
    <Dialog
      open={open}
      onClose={step === 'details' ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      dir={dir}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
        },
      }}
    >
      {/* Header with SATIM branding */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#fff',
          p: 2.5,
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                backgroundColor: 'rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <SecurityIcon sx={{ color: '#38bdf8', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ letterSpacing: 0.5 }}>
                SATIM E-Paiement Sécurisé
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LockIcon sx={{ fontSize: 13, color: '#10b981' }} /> Chiffrement SSL 256-bit • CIB & Edahabia
              </Typography>
            </Box>
          </Box>
          {step === 'details' && (
            <IconButton onClick={onClose} sx={{ color: '#94a3b8', '&:hover': { color: '#fff' } }}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* STEP 1: Card Details */}
        {step === 'details' && (
          <Box>
            {/* Sandbox Quick Buttons */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 2.5,
                borderRadius: 2,
                bgcolor: '#f8fafc',
                borderColor: '#e2e8f0',
              }}
            >
              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                🛠️ {t('subscription.satim.testSandbox', 'Mode Test Sandbox (Remplissage rapide) :')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={fillTestEdahabia}
                  sx={{
                    borderColor: '#f59e0b',
                    color: '#b45309',
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  💳 Carte Edahabia (Poste)
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={fillTestCib}
                  sx={{
                    borderColor: '#0284c7',
                    color: '#0369a1',
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  💳 Carte CIB (Interbancaire)
                </Button>
              </Box>
            </Paper>

            {/* Virtual Card Preview */}
            <Box
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 3,
                background: isEdahabia
                  ? 'linear-gradient(135deg, #b45309 0%, #d97706 50%, #f59e0b 100%)'
                  : 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #3b82f6 100%)',
                color: '#fff',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s ease',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <SimCardIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                <Chip
                  label={cardBrand}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    color: '#fff',
                    fontWeight: 'bold',
                    backdropFilter: 'blur(4px)',
                  }}
                />
              </Box>

              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'monospace',
                  letterSpacing: 3,
                  mb: 2,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                {cardNumber || '•••• •••• •••• ••••'}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                    Titulaire de la carte
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                    {cardHolder || 'NOM DU TITULAIRE'}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                    Expire fin
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                    {cardExpiry || 'MM/AA'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Inputs */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('subscription.satim.cardNumber', 'Numéro de carte (16 chiffres)')}
                  value={cardNumber}
                  onChange={handleCardNumberChange}
                  placeholder="6035 7812 3456 7890"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditCardIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('subscription.satim.cardExpiry', 'Date d’expiration (MM/AA)')}
                  value={cardExpiry}
                  onChange={handleExpiryChange}
                  placeholder="MM/AA"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('subscription.satim.cardCvv', 'CVV2 (3 chiffres)')}
                  value={cardCvv}
                  onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  placeholder="123"
                  type="password"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('subscription.satim.cardHolder', 'Nom complet du porteur')}
                  value={cardHolder}
                  onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
                  placeholder="NOM PRÉNOM"
                />
              </Grid>
            </Grid>

            {/* Order summary box */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mt: 2.5,
                borderRadius: 2,
                bgcolor: '#f1f5f9',
                borderColor: '#cbd5e1',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('subscription.plan', 'Forfait sélectionné')} :
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {plan.PLN_NAME} ({billingCycle === 'yearly' ? t('subscription.yearly', 'Annuel') : t('subscription.monthly', 'Mensuel')})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('subscription.totalDue', 'Montant total à débiter')} :
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {amount.toLocaleString('fr-DZ')} {plan.PLN_CURRENCY}
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}

        {/* STEP 2: 3D Secure OTP Simulation */}
        {step === 'otp' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#eff6ff',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <VerifiedUserIcon sx={{ fontSize: 36 }} />
            </Box>

            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Authentification 3D-Secure (SATIM)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
              Un mot de passe à usage unique (OTP) a été envoyé par SMS sur le numéro lié à votre carte {cardBrand}.
            </Typography>

            <Alert severity="info" sx={{ mb: 3, textAlign: 'left', borderRadius: 2 }}>
              💡 <b>Simulation Sandbox :</b> Le code SMS reçu est <code>{simulatedOtp}</code>.
            </Alert>

            <TextField
              label={t('subscription.satim.enterOtp', 'Code de vérification (OTP)')}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.trim())}
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', letterSpacing: 8, fontSize: '1.5rem', fontWeight: 'bold' },
              }}
              sx={{ maxWidth: 280, mx: 'auto', display: 'block', mb: 2 }}
            />

            <Typography variant="caption" color="text.secondary" display="block">
              N° de commande : <b>{orderNumber}</b>
            </Typography>
          </Box>
        )}

        {/* STEP 3: Success Screen */}
        {step === 'success' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: '#ecfdf5',
                color: '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48 }} />
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ color: '#065f46', mb: 1 }}>
              Paiement Confirmé avec Succès !
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Votre abonnement au forfait <b>{plan.PLN_NAME}</b> a été activé immédiatement.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', maxWidth: 420, mx: 'auto', textAlign: 'left' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Code d’autorisation SATIM :</Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>{approvalCode}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Moyen de règlement :</Typography>
                <Typography variant="caption" fontWeight="bold">Carte {cardBrand}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Montant débité :</Typography>
                <Typography variant="caption" fontWeight="bold" color="primary">{amount.toLocaleString('fr-DZ')} {plan.PLN_CURRENCY}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">Statut :</Typography>
                <Chip label="ACTIF / PAYÉ" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        {step === 'details' && (
          <>
            <Button onClick={onClose} color="inherit" disabled={loading}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              variant="contained"
              onClick={handleProceedToOtp}
              disabled={loading || !cardNumber || !cardExpiry || !cardCvv}
              endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
              sx={{
                bgcolor: '#0f172a',
                '&:hover': { bgcolor: '#1e293b' },
                px: 3,
                fontWeight: 'bold',
              }}
            >
              {loading ? t('subscription.satim.processing', 'Connexion SATIM...') : t('subscription.satim.payNow', 'Procéder au Paiement')}
            </Button>
          </>
        )}

        {step === 'otp' && (
          <>
            <Button onClick={() => setStep('details')} color="inherit" disabled={loading}>
              {t('common.back', 'Retour')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmPayment}
              disabled={loading || !otpCode}
              endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
              sx={{ px: 3, fontWeight: 'bold' }}
            >
              {loading ? t('subscription.satim.validating', 'Vérification...') : t('subscription.satim.confirmPayment', 'Confirmer et Débiter')}
            </Button>
          </>
        )}

        {step === 'success' && (
          <Button
            variant="contained"
            color="success"
            onClick={onClose}
            fullWidth
            sx={{ fontWeight: 'bold', py: 1 }}
          >
            {t('subscription.satim.continueToPortal', 'Accéder à mon Académie')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
